import React from 'react';
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';
import { Lock, Shield, Clock, ChartBar } from 'lucide-react';
import { EnhancedSEO } from '@/components/seo/EnhancedSEO';

export const ContractLocker: React.FC = () => {
  const title = "Smart Contract Locker Guide: Secure Your DeFi Project";
  const description = "Learn how to lock liquidity and tokens using smart contract lockers. Prevent rug pulls and build trust with your community.";
  const keywords = "smart contract locker, liquidity locker, token locker, DeFi security, rug pull prevention, contract security, BSC locker, ETH locker, LP token lock, vesting contract";

  const howToStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "description": description,
    "datePublished": "2024-12-15",
    "dateModified": "2024-12-15",
    "author": {
      "@type": "Person",
      "name": "Daniel T.",
      "url": "https://mdbapp.io/authors/dtdevmdbappio"
    },
    "step": [
      {
        "@type": "HowToStep",
        "name": "Understanding Contract Locking",
        "text": "Learn what contract locking is and why it's important for DeFi security"
      },
      {
        "@type": "HowToStep",
        "name": "Liquidity Locking",
        "text": "How to lock liquidity pool tokens to prevent rug pulls"
      },
      {
        "@type": "HowToStep",
        "name": "Token Locking",
        "text": "Implementing token locks for team tokens and vesting schedules"
      },
      {
        "@type": "HowToStep",
        "name": "Verification",
        "text": "How to verify locked tokens and contracts on the blockchain"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "MDB App Contract Locker",
        "description": "Our secure contract locking platform"
      },
      {
        "@type": "HowToTool",
        "name": "Lock Verification Tool",
        "description": "Verify locked contracts and tokens"
      }
    ]
  };

  return (
    <>
      <EnhancedSEO
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl="https://mdbapp.io/blog/contract-locker"
        ogType="article"
        publishDate="2024-12-15T08:00:00+00:00"
        modifiedDate="2024-12-15T08:00:00+00:00"
        author="Daniel T."
        section="Security"
        tags={["Smart Contracts", "DeFi Security", "Liquidity Locking", "Token Vesting"]}
        imageUrl="https://mdbapp.io/blog/contract-locker-guide.jpg"
        structuredData={howToStructuredData}
      />

      <div className="max-w-4xl mx-auto space-y-8">
        <article className="prose prose-gray dark:prose-invert max-w-none">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">
              Smart Contract Locker: Secure Your DeFi Project
            </h1>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-r from-amber-500 to-orange-500 p-[2px]">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-background flex items-center justify-center">
                  <span className="text-amber-500 font-bold text-lg">DC</span>
                </div>
              </div>
              <div>
                <p className="font-medium">D. Cram (MDB Dev)</p>
                <p className="text-sm">Published on January 8, 2025</p>
              </div>
            </div>
          </header>

          <p className="text-muted-foreground">Published on January 8, 2025 • Last Updated January 8, 2025 • 10 min read</p>

          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mt-4">
            <p className="font-semibold text-gray-900 dark:text-gray-100">Quick Navigation:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700 dark:text-gray-300">
              <li><a href="#introduction">Introduction to Contract Locker</a></li>
              <li><a href="#features">Key Features</a></li>
              <li><a href="#locking-process">Locking Process</a></li>
              <li><a href="#security">Security Measures</a></li>
              <li><a href="#analytics">Lock Analytics</a></li>
            </ul>
          </div>

          <div className="bg-amber-500/10 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-500/20 mt-6">
            <p className="font-semibold text-amber-700 dark:text-amber-400">🔒 Lock Your Contract:</p>
            <p className="text-gray-700 dark:text-gray-300">Secure your smart contract with our professional locking platform. <a href="https://mdbapp.io/smartcontractlocker" className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 underline" target="_blank" rel="noopener noreferrer">Launch Contract Locker</a></p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700 dark:text-amber-400/90">
              <li>Support for ETH & BSC networks</li>
              <li>Customizable lock periods</li>
              <li>Real-time lock status tracking</li>
              <li>Automated security verification</li>
              <li>Detailed analytics dashboard</li>
            </ul>
          </div>

          <h2 id="introduction" className="text-2xl font-semibold mt-8 mb-4 text-amber-500">Introduction to Contract Locker</h2>
          <p>
            The MDB Contract Locker provides a secure and transparent way to lock smart contracts on both Ethereum and BNB Chain networks. 
            Our platform is designed to build trust between projects and their communities by ensuring contract 
            immutability for specified periods.
          </p>

          <h2 id="features" className="text-2xl font-semibold mt-8 mb-4 text-amber-500">Key Features</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-amber-500" />
                  <h3 className="text-xl font-semibold text-amber-400">Flexible Locking</h3>
                </div>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Customizable lock durations</li>
                  <li>Multiple lock instances</li>
                  <li>Automatic lock enforcement</li>
                </ul>
              </div>

              <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <h3 className="text-xl font-semibold text-amber-400">Enhanced Security</h3>
                </div>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Multi-signature support</li>
                  <li>Time-lock mechanisms</li>
                  <li>Emergency unlock safeguards</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 id="locking-process" className="text-2xl font-semibold mt-8 mb-4 text-amber-500">Locking Process</h2>
          <p>
            Our streamlined locking process ensures maximum security with minimum complexity:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-amber-400">Contract Verification:</strong> Automated contract validation
            </li>
            <li>
              <strong className="text-amber-400">Lock Configuration:</strong> Set custom lock parameters
            </li>
            <li>
              <strong className="text-amber-400">Security Checks:</strong> Multiple verification layers
            </li>
            <li>
              <strong className="text-amber-400">Lock Confirmation:</strong> On-chain lock verification
            </li>
          </ul>

          <h2 id="security" className="text-2xl font-semibold mt-8 mb-4 text-amber-500">Security Measures</h2>
          <div className="space-y-4">
            <p>
              Our platform implements multiple security layers:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Smart contract auditing</li>
              <li>Time-locked withdrawals</li>
              <li>Multi-signature requirements</li>
              <li>Emergency security protocols</li>
            </ul>
          </div>

          <h2 id="analytics" className="text-2xl font-semibold mt-8 mb-4 text-amber-500">Lock Analytics</h2>
          <div className="space-y-4">
            <p>Track your locked contracts with detailed analytics:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Real-time lock status</li>
              <li>Lock duration tracking</li>
              <li>Historical lock data</li>
              <li>Community trust metrics</li>
            </ul>
          </div>

          <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-500/20 mt-8">
            <p className="font-semibold text-amber-400">💡 Pro Tip:</p>
            <p>
              For maximum security, consider implementing multiple locks with different durations and using our 
              multi-signature feature for critical contract locks.
            </p>
          </div>

          <div className="mt-8 p-4 border-t border-amber-500/20">
            <p className="text-sm text-muted-foreground">
              The MDB Contract Locker is continuously updated to maintain the highest security standards on both Ethereum and BNB Chain networks. 
              Our platform ensures your contracts remain secure and immutable throughout their lock period.
            </p>
          </div>
        </article>
      </div>
    </>
  );
};

export default ContractLocker;
